import { useState, type FormEvent } from "react";

interface NewsletterFormProps {
  tone?: "dark" | "light";
  compact?: boolean;
}

export default function NewsletterForm({ tone = "light", compact = false }: NewsletterFormProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const isDark = tone === "dark";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    formData.delete("website_alt");
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/daihs4rp14a1h3mr74hg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "订阅失败，请稍后重试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className={`flex flex-col sm:flex-row gap-2 ${compact ? "" : "max-w-md"}`}>
        <label className="sr-only" htmlFor="newsletter-email">
          邮箱地址
        </label>
        <input
          id="newsletter-email"
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="your@email.com"
          className={`flex-1 h-11 px-4 rounded-md text-sm outline-none border transition-colors ${
            isDark
              ? "bg-primary-900/60 border-primary-800 text-background-100 placeholder:text-background-200/60 focus:border-accent-400"
              : "bg-background-50 border-background-300 text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400"
          }`}
        />
        <input
          type="text"
          name="website_alt"
          className="form-field-aux"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className={`h-11 px-6 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
            isDark
              ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
              : "bg-primary-500 text-background-50 hover:bg-primary-600"
          }`}
        >
          {status === "submitting" ? "提交中…" : "订阅看点日报"}
        </button>
      </div>

      <div className="mt-2.5 min-h-[20px]">
        {status === "success" && (
          <p className={`text-xs flex items-center gap-1.5 ${isDark ? "text-accent-300" : "text-secondary-700"}`}>
            <i className="ri-checkbox-circle-line"></i>
            订阅成功，明早的看点精选会发送到你的邮箱。
          </p>
        )}
        {status === "error" && (
          <p className={`text-xs flex items-center gap-1.5 ${isDark ? "text-accent-300" : "text-primary-600"}`}>
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        )}
        {status === "idle" && (
          <p className={`text-xs ${isDark ? "text-background-200/70" : "text-foreground-500"}`}>
            每天一封，随时可退订。我们不会向第三方分享你的邮箱。
          </p>
        )}
      </div>
    </form>
  );
}